/**
 * Algopix Output Column Mapping
 *
 * Algopix batch output files have a "phrase output" sheet with ~106 columns.
 * This module maps those column headers (flexible, case-insensitive) to the
 * indexed fields in the products table. All columns are always stored verbatim
 * in algopix_data JSONB regardless of whether they're mapped.
 *
 * Column aliases are tried in order; first match wins.
 * Plain-English headers from the current file format come first,
 * followed by camelCase / legacy aliases for backward compatibility.
 * Add new aliases here as new Algopix file versions are encountered.
 *
 * W1 (Phase Wellspring): ALGOPIX_FIELD_MAP + buildColumnIndex are NO LONGER the
 * load-bearing import mapping — the per-distributor `column_map_registry` is
 * (see buildColumnIndexFromRegistry). They are retained as a heuristic suggester
 * for the brand-check preview (Files.tsx) and the column-map pre-fill.
 */

import { normalizeUpc } from './upcNormalize';

/**
 * Shape of the partial product object returned by `mapRowToProduct`.
 * Always carries `algopix_data` (the raw row JSON); extracted fields are
 * coerced to their final types (number, integer, boolean, or trimmed string).
 */
export interface MappedAlgopixProduct {
  algopix_data: Record<string, unknown>;
  upc?: string | null;
  asin?: string | null;
  [extraField: string]: unknown;
}

// ── Field → possible header aliases (lowercase, trimmed) ─────────────────────
const ALGOPIX_FIELD_MAP: Record<string, string[]> = {
  // Identifiers
  upc: [
    'original search term',
    'upcs',
    'upc code',
    'input upc',
    'phrase',
    'upc',
    'identifieditemupc',
    'identified item upc',
    'upccode',
    'input phrase',
  ],
  asin: [
    'asin',
    'amazon us asin',
    'amazon asin',
    'amazonsearchedasin',
    'amazon searched asin',
    'amazon_asin',
  ],
  ean: [
    'eans',
    'ean',
    'ean code',
    'identifieditemean',
    'identified item ean',
    'eancode',
    'ean_code',
  ],
  mpn: [
    'mpn',
    'manufacturer part number',
    'identifieditemmpn',
    'identified item mpn',
    'model number',
    'model_number',
  ],
  supplier_part_no: [
    'supplier part no',
    'supplier part number',
    'supplier sku',
    'item number',
    'part number',
    'part_number',
    'item_number',
  ],

  // Product identity
  title: [
    'product title',
    'title',
    'identified item title',
    'identifieditemtitle',
    'item title',
    'name',
    'product name',
    'item name',
  ],
  brand: ['brand name', 'brand', 'identified item brand', 'identifieditembrand', 'manufacturer'],
  category_algopix: [
    'taxonomy: amazon us category tree',
    'category',
    'identified item category',
    'identifieditemcategory',
    'product category',
    'algopix category',
    'item category',
    'google product category',
    'google_product_category',
  ],
  model: ['model'],
  color: ['color', 'colour'],

  // Cost / pricing
  cost_usd: [
    'original product cost ($usd)',
    'itemcost',
    'item cost',
    'cost',
    'input cost',
    'unit cost',
    'price',
    'purchase price',
    'item_cost',
  ],
  msrp: ['msrp (usd)', 'msrp', 'retail price', 'suggested retail price', 'srp'],
  map_price: ['map', 'minimum advertised price', 'minimum advertised price usd', 'map_price'],

  // ── Amazon ────────────────────────────────────────────────────────────────
  amazon_buy_box_price: [
    'amazon us buy box price (usd)',
    'amazon us price (usd)',
    'amazonbuyboxprice',
    'amazon buy box price',
    'amazon buybox price',
    'amazon bb price',
    'buy box price',
    'amazon_buy_box_price',
    'amazon price',
  ],
  amazon_referral_fee: [
    'amazon us market fee (usd)',
    'amazonreferralfeeamount',
    'amazon referral fee amount',
    'amazon referral fee',
    'referral fee',
    'amazonreferralfeepct',
    'amazon referral fee %',
    'referral_fee',
    'amazon_referral_fee',
  ],
  amazon_fba_fee: [
    'amazon us fba fee (usd)',
    'amazonfulfillmentfeeamount',
    'amazon fulfillment fee amount',
    'amazon fba fee',
    'fba fee',
    'amazonfbafee',
    'fulfillment fee',
    'fba_fee',
    'amazon_fba_fee',
    'amazon fulfillment fee',
  ],
  amazon_monthly_sales: [
    'amazon us estimated unit sales per month',
    'amazonmonthlysalditems',
    'amazon monthly sold items',
    'amazon monthly sales',
    'amazonmonthlysales',
    'amazon sales/month',
    'amazon_monthly_sales',
    'amazon monthly units sold',
  ],
  amazon_seller_count: [
    'amazon us offerings',
    'amazonsellerscount',
    'amazon sellers count',
    'amazon seller count',
    'seller count',
    'sellers count',
    'amazon_seller_count',
    'number of amazon sellers',
  ],
  amazon_demand_rating: [
    'amazon us demand',
    'amazondemandrating',
    'amazon demand rating',
    'demand rating',
    'amazon_demand_rating',
  ],
  amazon_offered_by_amazon: [
    'offered by amazon us',
    'amazonbbofferedbyamazon',
    'amazon bb offered by amazon',
    'offered by amazon',
    'amazon sells',
    'sold by amazon',
    'amazon_sold_by_amazon',
    'is amazon seller',
  ],
  amazon_profit_pct: ['amazon us % profit', 'amazon profit %', 'amazon profit pct'],
  amazon_listing_url: ['amazon us listing url', 'amazon listing url', 'amazon url'],

  // ── eBay ──────────────────────────────────────────────────────────────────
  ebay_price: [
    'ebay us price (usd)',
    'ebayavgsoldprice',
    'ebay avg sold price',
    'ebay price',
    'ebay avg price',
    'ebay sold price',
    'ebay_price',
    'ebay average sold price',
  ],
  ebay_monthly_sales: [
    'ebay us estimated unit sales per month',
    'ebaymonthlysalditems',
    'ebay monthly sold items',
    'ebay monthly sales',
    'ebaymonthlysales',
    'ebay_monthly_sales',
    'ebay monthly units sold',
  ],
  ebay_profit_pct: ['ebay us % profit', 'ebay profit %', 'ebay profit pct'],
  ebay_listing_url: ['ebay us listing url', 'ebay listing url', 'ebay url'],

  // ── Walmart ───────────────────────────────────────────────────────────────
  walmart_price: [
    'walmart us price (usd)',
    'walmarttprice',
    'walmart price',
    'walmartprice',
    'walmart_price',
    'walmart item price',
  ],
  walmart_monthly_sales: [
    'walmart us estimated unit sales per month',
    'walmartmonthlysalditems',
    'walmart monthly sold items',
    'walmart monthly sales',
    'walmartmonthlysales',
    'walmart_monthly_sales',
  ],
  walmart_profit_pct: ['walmart us % profit', 'walmart profit %', 'walmart profit pct'],
  walmart_listing_url: ['walmart us listing url', 'walmart listing url', 'walmart url'],

  // ── Dimensions ────────────────────────────────────────────────────────────
  weight_lbs: [
    'weight (pound)',
    'package weight (pound)',
    'packageweight',
    'package weight',
    'weight',
    'weight (lbs)',
    'item weight',
    'shipping weight',
    'weight_lbs',
    'weight lbs',
  ],
  length_in: [
    'length (inch)',
    'package length (inch)',
    'packagelength',
    'package length',
    'length',
    'length (in)',
    'item length',
    'length_in',
  ],
  width_in: [
    'width (inch)',
    'package width (inch)',
    'packagewidth',
    'package width',
    'width',
    'width (in)',
    'item width',
    'width_in',
  ],
  height_in: [
    'height (inch)',
    'package height (inch)',
    'packageheight',
    'package height',
    'height',
    'height (in)',
    'item height',
    'height_in',
  ],

  // ── Media ─────────────────────────────────────────────────────────────────
  image_url: ['image url', 'image', 'product image', 'product image url', 'thumbnail', 'photo url'],

  // ── Match / recommendation ────────────────────────────────────────────────
  algopix_recommendation: [
    'recommended',
    'algopixrecommendation',
    'algopix recommendation',
    'recommendation',
    'algopix_recommendation',
  ],
  algopix_id_type: [
    'identification type amazon us',
    'algopixidtype',
    'algopix id type',
    'id type',
    'idtype',
    'match type',
    'algopix_id_type',
    'identification type',
  ],
};

// Build reverse lookup: normalised header → product field name
const HEADER_INDEX: Record<string, string> = {};
for (const [field, aliases] of Object.entries(ALGOPIX_FIELD_MAP)) {
  for (const alias of aliases) {
    HEADER_INDEX[alias.toLowerCase().trim()] = field;
  }
}

/**
 * Heuristic header->field guesser (W1: demoted from the load-bearing import map).
 * Given an array of raw header strings, returns an array of the same length where
 * each element is the guessed product field name, or null if unknown. Used only
 * as a SUGGESTER now (the brand-check preview + the Phase-3 column-map pre-fill);
 * the import path uses buildColumnIndexFromRegistry against confirmed registry rows.
 */
export function buildColumnIndex(
  headers: ReadonlyArray<string | null | undefined>
): (string | null)[] {
  return headers.map(
    (h) =>
      HEADER_INDEX[
        String(h ?? '')
          .toLowerCase()
          .trim()
      ] ?? null
  );
}

/**
 * Map a single raw row object (keys = header names) to a partial product record.
 *
 * - Stores all columns verbatim in algopix_data JSONB.
 * - Extracts mapped fields into top-level product columns with type coercion.
 * - Does NOT include id, isg_sku, import_id, distributor_id (caller adds those).
 */
export function mapRowToProduct(
  row: Record<string, unknown>,
  headers: ReadonlyArray<string | null | undefined>,
  columnFieldMap: ReadonlyArray<string | null>
): MappedAlgopixProduct {
  // Store entire row verbatim (all columns).
  const algopix_data: Record<string, unknown> = { ...row };

  const product: MappedAlgopixProduct = { algopix_data };

  for (let i = 0; i < headers.length; i++) {
    const field = columnFieldMap[i];
    if (!field) continue;
    const headerKey = headers[i];
    if (headerKey == null) continue;
    const raw = row[headerKey];
    if (raw === undefined || raw === null || raw === '') continue;

    switch (field) {
      // Numeric (decimal)
      case 'cost_usd':
      case 'msrp':
      case 'map_price':
      case 'amazon_buy_box_price':
      case 'amazon_referral_fee':
      case 'amazon_fba_fee':
      case 'amazon_profit_pct':
      case 'ebay_price':
      case 'ebay_profit_pct':
      case 'walmart_price':
      case 'walmart_profit_pct':
      case 'weight_lbs':
      case 'length_in':
      case 'width_in':
      case 'height_in': {
        const n = parseFloat(String(raw).replace(/[^0-9.-]/g, ''));
        product[field] = isNaN(n) ? null : n;
        break;
      }
      // Numeric (integer)
      case 'amazon_monthly_sales':
      case 'amazon_seller_count':
      case 'ebay_monthly_sales':
      case 'walmart_monthly_sales': {
        const n = parseInt(String(raw).replace(/[^0-9]/g, ''), 10);
        product[field] = isNaN(n) ? null : n;
        break;
      }
      // Boolean
      case 'amazon_offered_by_amazon': {
        const s = String(raw).toLowerCase().trim();
        product[field] = s === 'true' || s === 'yes' || s === '1';
        break;
      }
      // UPC — deterministic normalization (W1): hyphen-strip + 11->12 leading-zero
      // pad; invalid values preserved raw (flagged downstream via isNormalizedUpc).
      // Normalizing in the coercion means dedup keys + the W2 lookup use the clean
      // value (this deliberately shifts raw->normalized dedup keys per §8/§139).
      case 'upc': {
        product[field] = normalizeUpc(String(raw)).value;
        break;
      }
      // String
      default:
        product[field] = String(raw).trim() || null;
    }
  }

  return product;
}

// ── Registry-driven column mapping (W1 — the load-bearing import map) ──────────

/**
 * One row of the per-distributor `column_map_registry`: the confirmed mapping of
 * one of this distributor's sheet headers to a canonical product field.
 */
export interface ColumnMapRegistryRow {
  canonical_field: string;
  sheet_header: string;
  is_cost_column?: boolean;
}

/**
 * Registry-driven analog of buildColumnIndex: given the sheet's header row and the
 * distributor's confirmed column_map_registry rows, return per-position canonical
 * field names (or null). Header match is case-insensitive + trimmed. This REPLACES
 * the global ALGOPIX_FIELD_MAP for the import path (the registry is per-distributor
 * and human-confirmed; no auto-guess of the cost column, pipeline-plan §8).
 */
export function buildColumnIndexFromRegistry(
  headers: ReadonlyArray<string | null | undefined>,
  registry: ReadonlyArray<ColumnMapRegistryRow>
): (string | null)[] {
  const headerToField = new Map<string, string>();
  for (const r of registry) {
    const key = String(r.sheet_header ?? '')
      .toLowerCase()
      .trim();
    if (key) headerToField.set(key, r.canonical_field);
  }
  return headers.map(
    (h) =>
      headerToField.get(
        String(h ?? '')
          .toLowerCase()
          .trim()
      ) ?? null
  );
}

/** The canonical product fields a sheet column may map to (the keys of the legacy
 *  alias map — still the valid canonical-field set for the registry + the UI). */
export const CANONICAL_FIELDS: string[] = Object.keys(ALGOPIX_FIELD_MAP);

/** Human labels for the column-map UI; acronyms overridden, else humanize the key. */
const CANONICAL_FIELD_LABELS: Record<string, string> = {
  upc: 'UPC',
  asin: 'ASIN',
  ean: 'EAN',
  mpn: 'MPN',
  supplier_part_no: 'Supplier Part #',
  title: 'Title',
  brand: 'Brand',
  category_algopix: 'Category',
  cost_usd: 'Cost (base, USD)',
  msrp: 'MSRP',
  map_price: 'MAP Price',
};

export function canonicalFieldLabel(field: string): string {
  return (
    CANONICAL_FIELD_LABELS[field] ??
    field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

// Sentinel headers that mark the real header row in an Algopix/price sheet (row 1
// is often an "AID" metadata row — see the header-detection note in useImport).
const HEADER_SENTINELS = new Set<string>([
  'phrase',
  'upc',
  'asin',
  'ean',
  'title',
  'brand',
  'itemcost',
  'item cost',
  'cost',
  'amazonbuyboxprice',
  'amazon buy box price',
  'amazonsearchedasin',
  'identifieditemtitle',
  'algopixrecommendation',
  'algopix recommendation',
  'ebayavgsoldprice',
  'walmarttprice',
]);

/**
 * Find the canonical header row in a sheet matrix: the first row (of the first 10)
 * carrying a known sentinel header; else the densest of the first 5 rows. SHARED by
 * useImport (the import parse) and the ingest column-map UI so the operator
 * maps the SAME header strings the import will index.
 */
export function findHeaderRow(matrix: ReadonlyArray<ReadonlyArray<unknown>>): number {
  for (let i = 0; i < Math.min(matrix.length, 10); i++) {
    const row = matrix[i] ?? [];
    if (row.some((c) => c != null && HEADER_SENTINELS.has(String(c).toLowerCase().trim()))) {
      return i;
    }
  }
  let bestScore = 0;
  let idx = 0;
  for (let i = 0; i < Math.min(matrix.length, 5); i++) {
    const score = (matrix[i] ?? []).filter(
      (c) => c !== null && c !== undefined && String(c).trim() !== ''
    ).length;
    if (score > bestScore) {
      bestScore = score;
      idx = i;
    }
  }
  return idx;
}
